import { useNavigate } from "react-router-dom";
import { Menu, LogOut, User, ChevronDown, Compass, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, initials } from "@/lib/constants";
import { NotificationBell } from "@/components/NotificationBell";
import { openAIWidget } from "@/components/AIChatWidget";
import { AI_ELIGIBLE } from "@/components/AIChat";
import { TasksBell } from "@/components/TasksBell";
import { InstallPWA } from "@/components/InstallPWA";
import { RecordCallButton } from "@/components/RecordCallButton";
import { HeaderCheckIn } from "@/components/HeaderCheckIn";
import { ScanCardButton } from "@/components/ScanCardDialog";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CALL_ROLES = ["super_admin", "sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc"];

export function Header({ onMenu, onStartTour }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200 no-print" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="h-16 px-4 sm:px-6 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={onMenu} className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-slate-100" data-testid="mobile-menu-btn">
          <Menu className="h-5 w-5 text-slate-700" />
        </button>

        <div className="flex items-center gap-2.5" data-testid="brand-univicoustic">
          <img src="/uvi-emblem.svg" alt="UniVicoustic" className="h-8 w-auto" />
          <span className="h-6 w-px bg-slate-200 hidden sm:block" />
          <img src="/uvi-wordmark.svg" alt="UniVicoustic" className="h-3 w-auto hidden sm:block" />
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {CALL_ROLES.includes(user?.role) && <RecordCallButton compact onSaved={() => {}} />}
        {CALL_ROLES.includes(user?.role) && <HeaderCheckIn />}
        <ScanCardButton compact />
        {AI_ELIGIBLE(user) && !user?.impersonated && (
          <button onClick={openAIWidget} title="Ask SalesCore AI"
            className="h-9 w-9 rounded-full flex items-center justify-center bg-slate-900 text-amber-300 hover:bg-slate-800 active:scale-95 transition-all"
            data-testid="header-ask-ai-btn">
            <Sparkles className="h-4.5 w-4.5" />
          </button>
        )}
        <InstallPWA />
        <ThemeToggle />
        {user?.role !== "factory" && user?.role !== "management" && <TasksBell />}
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
              <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">
                {initials(user?.name)}
              </span>
              <span className="hidden sm:flex flex-col items-start leading-none">
                <span className="text-xs font-medium text-slate-800">{user?.name}</span>
                <span className="text-[10px] text-slate-400">{ROLE_LABELS[user?.role]}</span>
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm">{user?.name}</span>
                <span className="text-xs text-slate-400 font-normal">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
              <User className="h-4 w-4 mr-2" /> Profile & Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onStartTour} data-testid="menu-tour">
              <Compass className="h-4 w-4 mr-2" /> Take a tour
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600" data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>
    </header>
  );
}
